import React from "react";
import { Topic, P, H3, H4, UL, Callout, Table, VisualBox } from "@/pages/knowledge/Atoms";

export default function Advanced() {
  return (
    <>
      <Topic id="algo-trading" title="What is Algo Trading?">
        <P>
          Algo Trading (Algorithmic Trading) uses computer programs to
          automatically buy and sell stocks, futures or options — without
          manual effort. In simple words, <b>trading using pre-set rules and
          logic</b> instead of emotions and guesswork.
        </P>

        <P>Rules can be based on:</P>
        <UL>
          <li>Price movements</li>
          <li>Technical indicators (RSI, MA, MACD)</li>
          <li>Volume</li>
          <li>Time</li>
          <li>Mathematical / statistical models</li>
        </UL>

        <H3>Simple example</H3>
        <Callout>
          Rule: &ldquo;Buy Nifty Futures when it rises above 26,000 and sell when
          it falls below 25,950.&rdquo; Your algo monitors continuously. The
          moment Nifty crosses 26,000 → it auto-buys; the moment it dips below
          25,950 → it auto-sells. Milliseconds, no emotions.
        </Callout>

        <H3>How algo trading works</H3>
        <Table
          headers={["Step", "Description"]}
          rows={[
            ["1. Strategy creation", "Define logic (e.g., MA crossover)"],
            ["2. Coding the strategy", "Python, C++ or a visual builder"],
            ["3. Backtesting", "Test on historical data"],
            ["4. Execution", "Connect to broker API; place live trades"],
            ["5. Monitoring", "Track performance and risk"],
          ]}
        />

        <H3>Algo trading in the Indian market</H3>
        <P>
          Regulated by SEBI. Used by institutional investors, prop firms,
          professional traders — and increasingly retail traders via modern
          algo platforms. Trades execute through NSE, BSE and MCX using APIs
          from brokers like Zerodha, Angel One, Dhan, IIFL, etc. Algo trading
          now contributes <b>more than 55%</b> of total trades on Indian
          exchanges.
        </P>

        <H3>Benefits</H3>
        <Table
          headers={["Benefit", "Description"]}
          rows={[
            ["Speed", "Executes in milliseconds"],
            ["Accuracy", "Eliminates human error"],
            ["Emotion-free", "Logic, not fear/greed"],
            ["Backtesting", "Test before risking money"],
            ["Efficiency", "Monitor multiple instruments at once"],
            ["Consistency", "Same rule, every time"],
          ]}
        />

        <H3>Risks &amp; challenges</H3>
        <Callout kind="warn">
          <UL>
            <li>Technical glitches (internet/server failures)</li>
            <li>Overfitting in backtests (works on history, fails live)</li>
            <li>Market volatility / sudden events</li>
            <li>Regulatory compliance — only SEBI-approved APIs</li>
          </UL>
        </Callout>

        <H3>Example: Moving Average Crossover</H3>
        <P>
          <b>Logic:</b> Buy when 20-day MA crosses above 50-day MA; sell when it
          crosses below. <b>Asset:</b> Nifty 50. <b>Timeframe:</b> 15-min chart.
          The algo keeps watching every 15 minutes and places orders instantly
          on a crossover — no emotion, no delay.
        </P>
      </Topic>

      <Topic id="adjustment" title="What is Adjustment in Trading?">
        <P>
          Adjustment means <b>modifying your existing position</b> to manage
          risk, reduce losses or lock in profits when the market moves
          unexpectedly. Instead of exiting a losing trade immediately, you
          adjust it to bring it back into a safer or profitable zone.
        </P>

        <H3>Simple example</H3>
        <P>
          You sold a Nifty 26,000 Call thinking the market will stay below
          26,000. You receive ₹100 premium. Nifty moves to <b>26,200</b> — your
          position is now in loss. You have two choices:
        </P>
        <UL>
          <li>Exit and book the loss, or</li>
          <li><b>Adjust</b> — sell another Put at 25,800 to collect extra premium.</li>
        </UL>
        <P>
          You&rsquo;ve now created a <b>short strangle</b> — earning premium
          from both sides. Extra premium balances the loss.
        </P>

        <H3>Common adjustment techniques</H3>
        <H4>1. Rolling up / down</H4>
        <P>
          Shift strikes higher (or lower) as the market moves. Example: short
          Nifty 26,000 CE; Nifty rises to 26,200 → buy back 26,000 CE and sell
          26,200 CE.
        </P>
        <H4>2. Adding opposite position (hedge / spread)</H4>
        <P>
          Call side losing? Add a Put. Bank Nifty short 57,000 CE under
          pressure? Add a 56,800 PE sell → balanced short strangle.
        </P>
        <H4>3. Converting to a range strategy</H4>
        <P>
          Convert a risky single leg into a multi-leg structure (iron condor,
          credit spread) to cap losses.
        </P>
        <H4>4. Rolling over to next expiry</H4>
        <P>
          Close current week&rsquo;s position and sell the same strike for next
          week — fresh premium, same view.
        </P>
        <H4>5. Using futures for quick adjustment</H4>
        <P>
          Short call in heavy loss due to a sharp rise? Buy Nifty Futures to
          hedge until the market stabilises.
        </P>

        <H3>Important rules</H3>
        <Callout kind="warn">
          <UL>
            <li>Pre-plan your adjustment level (e.g., 100 points against you)</li>
            <li>Adjustments should reduce risk — never increase it</li>
            <li>Avoid over-adjusting — extra brokerage &amp; confusion</li>
            <li>Track Greeks; balance Delta &amp; Theta</li>
            <li>Stick to strategy view — adjust only on real trend changes</li>
          </UL>
        </Callout>

        <Quote>
          The market doesn&rsquo;t punish traders who are wrong — it punishes
          those who don&rsquo;t adjust.
        </Quote>
      </Topic>

      <Topic id="hedging" title="What is Hedging?">
        <P>
          Hedging means <b>protecting your trades or investments</b> from
          potential losses. Think of it as insurance — you take another position
          that gains if your original trade loses.
        </P>

        <H3>Simple example</H3>
        <P>
          You hold Reliance shares worth ₹5,00,000. Worried about a short-term
          fall? Buy a Reliance ₹1,200 Put. If Reliance falls to ₹1,150, the Put
          gains and offsets the loss on shares. If Reliance rises, you lose only
          the small premium.
        </P>

        <H3>Hedging in the Indian market</H3>
        <UL>
          <li>Retail traders — protect Nifty / Bank Nifty / stock positions</li>
          <li>Institutions — secure crore-sized portfolios</li>
          <li>Exporters/importers — guard against currency swings</li>
          <li>Commodity traders — gold, crude oil</li>
        </UL>

        <H3>Types of hedging</H3>
        <Table
          headers={["Type", "How it works", "Indian example"]}
          rows={[
            ["Futures hedging", "Opposite position in futures", "Own TCS shares → sell TCS Futures"],
            ["Options hedging", "Use call/put options", "Hold Nifty ETF → buy Nifty Put"],
            ["Portfolio hedging", "Use index derivatives", "Diverse portfolio → buy Nifty Puts"],
            ["Cross hedging", "Hedge using related asset", "Tata Steel → hedge via Nifty Metal Futures"],
          ]}
        />

        <Callout kind="info">
          Hedging has a cost (premium / margin) and may cap upside — it&rsquo;s a
          defensive tool, not a profit-making strategy.
        </Callout>

        <Quote>
          Good traders focus on making profits, but great traders focus on
          protecting them.
        </Quote>
      </Topic>

      <Topic id="risk-management" title="What is Risk Management?">
        <P>
          Risk Management means protecting your capital by controlling how much
          you can lose on any single trade or overall. It&rsquo;s not about
          avoiding losses — it&rsquo;s about ensuring one loss doesn&rsquo;t wipe
          out your account.
        </P>

        <H3>Simple example</H3>
        <P>
          You have ₹1,00,000. You decide &ldquo;I will never risk more than
          ₹2,000 per trade.&rdquo; This forces you to reduce position size or
          tighten your stop-loss. Even after 5–6 bad trades, your account
          survives.
        </P>

        <H3>The 8 core practices</H3>
        <H4>1. Use a stop-loss in every trade</H4>
        <P>
          Buy Reliance at ₹1,250; stop-loss at ₹1,238 → max loss ₹12/share.
        </P>
        <H4>2. Risk only 1–2% of capital per trade</H4>
        <H4>3. Position sizing</H4>
        <P>
          Stop-loss ₹20, risk limit ₹2,000 → take only 100 shares.
        </P>
        <H4>4. Diversify</H4>
        <P>Spread across Bank Nifty, Nifty, 1–2 stocks like Reliance / TCS.</P>
        <H4>5. Hedge</H4>
        <P>
          Hold Infosys, worried about correction? Buy a Put.
        </P>
        <H4>6. Daily / weekly loss limit</H4>
        <P>If your daily limit is ₹5,000, stop trading once hit — no revenge trades.</P>
        <H4>7. Avoid over-leveraging</H4>
        <H4>8. Review &amp; learn</H4>

        <H3>Example risk-management plan</H3>
        <Table
          headers={["Rule", "Purpose"]}
          rows={[
            ["Risk 2% per trade", "Keeps losses small"],
            ["Stop-loss mandatory", "Controls downside"],
            ["Max 3 trades per day", "Avoids overtrading"],
            ["No new trade after 5% daily loss", "Limits emotional trading"],
            ["Weekly review", "Improves future decisions"],
          ]}
        />

        <Callout kind="danger">
          <b>Common mistakes:</b> no stop-loss, doubling down on losers, trading
          too big, ignoring volatility, letting emotions decide exits.
        </Callout>

        <Quote>Protect your capital first — profits will automatically follow.</Quote>
      </Topic>

      <Topic id="quant-trading" title="What is Quant Trading?">
        <P>
          Quant Trading (Quantitative Trading) uses mathematics, statistics and
          computer programs to make trading decisions — instead of emotions or
          guesswork.
        </P>
        <Callout>
          <b>Quant Trading = Data + Maths + Technology + Discipline.</b>
        </Callout>

        <H3>How it works (step-by-step)</H3>
        <Table
          headers={["Step", "Description", "Example"]}
          rows={[
            ["Data collection", "Gather historical / live data", "Nifty, Bank Nifty, Reliance, Infosys"],
            ["Strategy building", "Statistical / technical model", "Mean reversion, momentum"],
            ["Backtesting", "Test on past data", "Check 5-year performance"],
            ["Execution", "Auto-trade via APIs", "Place orders in real time"],
            ["Risk control", "Code-based risk rules", "Position limits, stop-losses"],
          ]}
        />

        <H3>Common quant strategies used in India</H3>
        <Table
          headers={["Strategy", "Description", "Indian example"]}
          rows={[
            ["Trend Following", "Follows market momentum", "Buy Nifty when above MA"],
            ["Mean Reversion", "Prices revert to average", "Sell Tata Motors if 5% above mean"],
            ["Arbitrage", "Profit from price differences", "NSE vs BSE quote gap"],
            ["Pair Trading", "Trade correlated stocks", "HDFC vs ICICI spread"],
            ["Event-based", "Trade around events", "Volatility around RBI policy"],
          ]}
        />

        <H3>Risks</H3>
        <Callout kind="warn">
          <UL>
            <li>Bad data → bad results</li>
            <li>Overfitting on history</li>
            <li>Requires solid programming + market knowledge</li>
          </UL>
        </Callout>
      </Topic>

      <Topic id="algo-vs-quant" title="Algo Trading vs Quant Trading">
        <P>
          The two are often confused. Quant trading <b>builds the brain</b> —
          creating ideas and models. Algo trading <b>acts as the body</b> —
          executing those ideas automatically.
        </P>
        <Table
          headers={["Aspect", "Algo Trading", "Quant Trading"]}
          rows={[
            ["Meaning", "Automated rule-based trade execution", "Data-driven strategy development"],
            ["Core function", "Execution", "Research & analysis"],
            ["Dependency", "Needs predefined logic", "Generates the trading logic"],
            ["Example", "Auto-buy Nifty when RSI < 30", "Model predicting RSI reversals"],
            ["Goal", "Fast, disciplined trading", "Statistically proven trading"],
            ["Outcome", "Automation", "Intelligence"],
          ]}
        />
        <Callout kind="success">
          <b>In short:</b> Quant trading decides <b>what</b> to trade. Algo
          trading decides <b>how and when</b> to trade.
        </Callout>
      </Topic>
    </>
  );
}

function Quote({ children }) {
  return (
    <blockquote className="border-l-4 border-[#084392] pl-4 italic text-[#475569] my-4">
      {children}
    </blockquote>
  );
}
